import { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { DeleteConfirmationModal } from '@/shared/ui/DeleteConfirmationModal';
import { useTranslation } from 'react-i18next';
import { TemplateNameSheet } from '@/features/templates-config';

interface EmailTemplatesActionMenuProps {
  slug: string;
  templateName: string;
  onRename: (slug: string, templateName: string) => void;
  onDelete: (slug: string) => void;
}

/**
 * Row actions for an email template.
 *
 * Every interactive element stops click propagation: Radix portals the menu and both modals out of
 * the table, but React synthetic events still bubble through the React tree, so without this a
 * click inside them reaches the row's navigate handler.
 */
export function EmailTemplatesActionMenu({
  slug,
  templateName,
  onRename,
  onDelete,
}: EmailTemplatesActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const { t: tCommon } = useTranslation('common');

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('knowledgeHub.emailTemplatesActionMenu.emailTemplateActions')}
            data-template-slug={slug}
            className="size-9 p-0"
            onClick={(event) => event.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
            <PencilIcon className="mr-2 size-4" />
            {t('knowledgeHub.emailTemplatesActionMenu.rename')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => setDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2Icon className="mr-2 size-4" />
            {t('knowledgeHub.emailTemplatesActionMenu.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- structural propagation guard; keyboard interaction lives on the dialogs themselves */}
      <div onClick={(event) => event.stopPropagation()}>
        {renameOpen && (
          <TemplateNameSheet
            mode="rename"
            initialName={templateName}
            onCancel={() => setRenameOpen(false)}
            onSubmit={(name) => {
              onRename(slug, name);
              setRenameOpen(false);
            }}
          />
        )}
        <DeleteConfirmationModal
          open={deleteOpen}
          onCancel={() => setDeleteOpen(false)}
          onConfirmDelete={() => {
            onDelete(slug);
            setDeleteOpen(false);
          }}
          resourceType={tCommon('components.deleteConfirmationModal.template')}
          resourceName={templateName}
          warningMessage={t('knowledgeHub.emailTemplatesActionMenu.thisActionCannotBeUndone')}
          data-testid="email-template-delete-modal"
        />
      </div>
    </>
  );
}
